'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useRouter } from 'next/router';
import { AppAuthLoader } from '@/components/AppGateShell';
import { adminLogout, getAdminSession } from '@/lib/adminSessionClient';

const AdminAuthContext = createContext(null);

function isAdminZone(pathname) {
  return pathname.startsWith('/admin');
}

function isAdminLoginPath(pathname) {
  return pathname === '/admin' || pathname === '/admin/';
}

/** Labels derived from session JWT (email / name). */
function adminProfileFromSession(email, name) {
  const e = typeof email === 'string' ? email.trim() : '';
  const n = typeof name === 'string' ? name.trim() : '';
  const displayEmail = e || '—';
  const displayName =
    n ||
    (e
      ? e
          .split('@')[0]
          .replace(/[._-]+/g, ' ')
          .replace(/\b\w/g, (c) => c.toUpperCase())
      : 'Admin');
  const avatarInitial = displayName.trim().charAt(0).toUpperCase() || 'A';
  return { displayName, displayEmail, avatarInitial };
}

export function AdminAuthProvider({ children }) {
  const router = useRouter();
  const pathname = router.pathname;
  const [state, setState] = useState({
    status: 'idle',
    role: null,
    email: null,
    name: null,
  });
  const authenticatedRef = useRef(false);

  useEffect(() => {
    authenticatedRef.current = state.status === 'authenticated';
  }, [state.status]);

  const routerRef = useRef(router);
  routerRef.current = router;

  useEffect(() => {
    const cancelledRef = { current: false };

    (async () => {
      if (!router.isReady) return;

      if (!isAdminZone(pathname)) {
        authenticatedRef.current = false;
        setState({ status: 'idle', role: null, email: null, name: null });
        return;
      }

      const loginOnly = isAdminLoginPath(pathname);
      // Already in admin — no mid-work session gate / background recheck.
      if (!loginOnly && authenticatedRef.current) {
        return;
      }

      // Never unmount an already-authenticated admin shell while rechecking.
      setState((s) =>
        s.status === 'authenticated' ? s : { ...s, status: 'loading' }
      );

      const session = await getAdminSession();
      if (cancelledRef.current) return;

      if (loginOnly) {
        if (session.ok) {
          authenticatedRef.current = true;
          setState({
            status: 'authenticated',
            role: session.role,
            email: session.email,
            name: session.name,
          });
          void routerRef.current.replace('/admin/dashboard');
        } else {
          authenticatedRef.current = false;
          setState({ status: 'unauthenticated', role: null, email: null, name: null });
        }
        return;
      }

      if (!session.ok) {
        authenticatedRef.current = false;
        setState({ status: 'unauthenticated', role: null, email: null, name: null });
        void routerRef.current.replace('/admin');
        return;
      }

      authenticatedRef.current = true;
      setState({
        status: 'authenticated',
        role: session.role,
        email: session.email,
        name: session.name,
      });
    })();

    return () => {
      cancelledRef.current = true;
    };
  }, [pathname, router.isReady]);

  const refresh = useCallback(async () => {
    if (!isAdminZone(pathname)) return;
    const loginOnly = isAdminLoginPath(pathname);
    // Don't flash the verifying gate when already inside the admin panel.
    if (!authenticatedRef.current) {
      setState((s) => (s.status === 'authenticated' ? s : { ...s, status: 'loading' }));
    }
    const session = await getAdminSession();
    if (loginOnly) {
      if (session.ok) {
        setState({
          status: 'authenticated',
          role: session.role,
          email: session.email,
          name: session.name,
        });
        router.replace('/admin/dashboard');
      } else {
        setState({ status: 'unauthenticated', role: null, email: null, name: null });
      }
      return;
    }
    if (!session.ok) {
      setState({ status: 'unauthenticated', role: null, email: null, name: null });
      router.replace('/admin');
      return;
    }
    setState({
      status: 'authenticated',
      role: session.role,
      email: session.email,
      name: session.name,
    });
  }, [pathname, router]);

  const signOut = useCallback(async () => {
    await adminLogout();
    setState({ status: 'unauthenticated', role: null, email: null, name: null });
    await router.push('/admin');
  }, [router]);

  const value = useMemo(() => {
    const { displayName, displayEmail, avatarInitial } = adminProfileFromSession(
      state.email,
      state.name
    );
    return {
      status: state.status,
      role: state.role,
      email: state.email,
      name: state.name,
      displayName,
      displayEmail,
      avatarInitial,
      isAuthenticated: state.status === 'authenticated',
      refresh,
      signOut,
    };
  }, [state.status, state.role, state.email, state.name, refresh, signOut]);

  const inAdmin = isAdminZone(pathname);
  const loginRoute = isAdminLoginPath(pathname);
  const protectedAdmin = inAdmin && !loginRoute;

  if (inAdmin) {
    if (protectedAdmin && state.status !== 'authenticated') {
      return (
        <AdminAuthContext.Provider value={value}>
          {state.status === 'loading' ? (
            <AppAuthLoader
              title="Verifying admin session"
              description="Confirming administrator access. This only takes a moment."
            />
          ) : (
            <div className="min-h-screen bg-bg-primary" aria-busy="true" />
          )}
        </AdminAuthContext.Provider>
      );
    }

    if (loginRoute && state.status !== 'unauthenticated') {
      return (
        <AdminAuthContext.Provider value={value}>
          {state.status === 'loading' || state.status === 'authenticated' ? (
            <AppAuthLoader
              title="Verifying admin session"
              description="Confirming administrator access. This only takes a moment."
            />
          ) : (
            <div className="min-h-screen bg-bg-primary" aria-busy="true" />
          )}
        </AdminAuthContext.Provider>
      );
    }
  }

  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
}

export function useAdminAuth() {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) {
    throw new Error('useAdminAuth must be used within AdminAuthProvider');
  }
  return ctx;
}
